// Connected accounts (OAuth 2.0 services, GET /integrations): link through the service's own page, unlink, and
// what each can do. Strava takes Tracks as activities, on request or each new recording automatically.
// Garmin Connect has its own calls (features/garmin).
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/api/client';
import { rememberReturnTo } from '@/auth/returnTo';

export type Provider = 'strava';

export interface Connection {
  provider: Provider;
  /** Set up on this server. */
  available: boolean;
  connected: boolean;
  /** The service refused the link: connect again. */
  needsRelink: boolean;
  displayName: string | null;
  linkedAt: string | null;
  /** Each new recorded track is posted to it without asking. */
  autoUpload: boolean;
  premiumFeature: string | null;
  capabilities: string[];
}

/** A track on Strava: its activity once Strava has made it. `error`: Strava refused it. */
export interface StravaStatus {
  activityId: number | null;
  processing: boolean;
  error: string | null;
}

/** A failed call; `code` is the API's (1057 not connected, 1058 link expired, 1060 service failed, ...). */
export class ConnectionError extends Error {
  constructor(
    readonly status: number,
    readonly code?: string,
  ) {
    super(`Connected account: ${status}${code ? ` (${code})` : ''}`);
  }
}

export const callbackPath = (provider: Provider) => `/connect/${provider}/callback`;
const callbackUrl = (provider: Provider) => `${window.location.origin}${callbackPath(provider)}`;
const connectionsKey = ['integrations'];

async function call(path: string, init: RequestInit = {}): Promise<Response> {
  const response = await apiFetch(`/integrations${path}`, {
    ...init,
    headers: init.body ? { 'Content-Type': 'application/json' } : undefined,
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { message?: string };
    throw new ConnectionError(response.status, body.message);
  }
  return response;
}

export function useConnections() {
  return useQuery<Connection[], ConnectionError>({
    queryKey: connectionsKey,
    queryFn: async () => (await (await call('')).json()) as Connection[],
  });
}

export const useConnection = (provider: Provider) => {
  const connections = useConnections();
  return { ...connections, data: connections.data?.find((c) => c.provider === provider) };
};

/** Goes to the service to approve the link; it returns to /connect/{provider}/callback, then back here. */
export function useConnect(provider: Provider) {
  return useMutation<void, ConnectionError>({
    mutationFn: async () => {
      const response = await call(`/${provider}/connect`, {
        method: 'POST',
        body: JSON.stringify({ callbackUrl: callbackUrl(provider) }),
      });
      const { authorizeUrl } = (await response.json()) as { authorizeUrl: string };
      rememberReturnTo(`${window.location.pathname}${window.location.search}`);
      window.location.assign(authorizeUrl);
    },
  });
}

/** Finishes the link with what the service sent to the callback page. */
export async function completeLink(provider: Provider, code: string, state: string) {
  await call(`/${provider}/complete`, {
    method: 'POST',
    body: JSON.stringify({ code, state, callbackUrl: callbackUrl(provider) }),
  });
}

export function useDisconnect(provider: Provider) {
  const queryClient = useQueryClient();
  return useMutation<void, ConnectionError>({
    mutationFn: async () => {
      await call(`/${provider}`, { method: 'DELETE' });
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: connectionsKey }),
  });
}

/** Turns "post new tracks automatically" on or off. */
export function useSetAutoUpload(provider: Provider) {
  const queryClient = useQueryClient();
  return useMutation<Connection, ConnectionError, boolean>({
    mutationFn: async (autoUpload) =>
      (await (
        await call(`/${provider}/settings`, { method: 'PUT', body: JSON.stringify({ autoUpload }) })
      ).json()) as Connection,
    onSuccess: (connection) =>
      queryClient.setQueryData<Connection[]>(connectionsKey, (list) =>
        list?.map((c) => (c.provider === connection.provider ? connection : c)),
      ),
  });
}

const stravaKey = (trackGuid: string) => ['strava', 'track', trackGuid];

/** Whether the track has been posted to Strava (null: not yet). */
export function useStravaStatus(trackGuid: string, enabled: boolean) {
  return useQuery<StravaStatus | null, ConnectionError>({
    queryKey: stravaKey(trackGuid),
    enabled,
    queryFn: async () => {
      const response = await call(`/strava/tracks/${trackGuid}`);
      return response.status === 204 ? null : ((await response.json()) as StravaStatus);
    },
  });
}

export function useUploadToStrava(trackGuid: string) {
  const queryClient = useQueryClient();
  return useMutation<StravaStatus, ConnectionError>({
    mutationKey: ['strava', 'upload', trackGuid],
    mutationFn: async () =>
      (await (await call(`/strava/tracks/${trackGuid}`, { method: 'POST' })).json()) as StravaStatus,
    onSuccess: (status) => queryClient.setQueryData(stravaKey(trackGuid), status),
    // The link may have been refused: show "Connect again".
    onError: () => void queryClient.invalidateQueries({ queryKey: connectionsKey }),
  });
}

export const invalidateConnections = (queryClient: ReturnType<typeof useQueryClient>) =>
  queryClient.invalidateQueries({ queryKey: connectionsKey });

/** Strava's page for an activity. */
export const stravaActivityUrl = (id: number) => `https://www.strava.com/activities/${id}`;

/** Strava's page for uploading a file by hand (when Strava can't be linked here yet). */
export const STRAVA_UPLOAD_URL = 'https://www.strava.com/upload/select';
